import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { teamAPI } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Users, UserPlus, MoreVertical, Shield, UserCog, UserMinus, Mail,
  Crown, UserCheck, User, Loader2, RefreshCw
} from 'lucide-react';

const ROLES = {
  owner: { label: 'Owner', color: 'bg-amber-100 text-amber-800', icon: Crown, description: 'Full access, can manage team and billing' },
  admin: { label: 'Admin', color: 'bg-blue-100 text-blue-800', icon: Shield, description: 'Can manage properties, bookings, and team members' },
  member: { label: 'Member', color: 'bg-gray-100 text-gray-800', icon: User, description: 'Can view and manage assigned properties' },
};

const STATUSES = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-800' },
  disabled: { label: 'Disabled', color: 'bg-red-100 text-red-800' },
};

export default function TeamPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'member' });

  const isOwner = user?.role === 'owner';
  const isAdmin = user?.role === 'admin' || isOwner;

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const res = await teamAPI.list();
      setMembers(res.data || []);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to load team members',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast({ title: 'Error', description: 'Name and email are required', variant: 'destructive' });
      return;
    }

    try {
      setInviting(true);
      await teamAPI.invite(inviteForm);
      toast({
        title: 'Invitation Sent',
        description: `Invitation email sent to ${inviteForm.email}`,
      });
      setInviteOpen(false);
      setInviteForm({ name: '', email: '', role: 'member' });
      loadTeamMembers();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to send invitation',
        variant: 'destructive',
      });
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (newRole) => {
    if (!selectedMember) return;
    try {
      await teamAPI.updateRole(selectedMember.user_id, newRole);
      toast({
        title: 'Role Updated',
        description: `${selectedMember.name}'s role changed to ${ROLES[newRole]?.label || newRole}`,
      });
      setRoleDialogOpen(false);
      loadTeamMembers();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to update role',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedMember) return;
    try {
      await teamAPI.updateStatus(selectedMember.user_id, newStatus);
      toast({
        title: 'Status Updated',
        description: `${selectedMember.name}'s account has been ${newStatus === 'active' ? 'enabled' : 'disabled'}`,
      });
      setStatusDialogOpen(false);
      loadTeamMembers();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleRemove = async () => {
    if (!selectedMember) return;
    try {
      await teamAPI.remove(selectedMember.user_id);
      toast({
        title: 'Member Removed',
        description: `${selectedMember.name} has been removed from the team`,
      });
      setRemoveDialogOpen(false);
      loadTeamMembers();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to remove member',
        variant: 'destructive',
      });
    }
  };

  const openRoleDialog = (member) => {
    setSelectedMember(member);
    setRoleDialogOpen(true);
  };

  const openStatusDialog = (member) => {
    setSelectedMember(member);
    setStatusDialogOpen(true);
  };

  const openRemoveDialog = (member) => {
    setSelectedMember(member);
    setRemoveDialogOpen(true);
  };

  const getRoleInfo = (role) => ROLES[role] || { label: role, color: 'bg-gray-100 text-gray-800', icon: User };
  const getStatusInfo = (status) => STATUSES[status] || STATUSES.active;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-airbnb-pink" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-500 mt-1">Manage your team and their access permissions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadTeamMembers} data-testid="refresh-team-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {isAdmin && (
            <Button onClick={() => setInviteOpen(true)} data-testid="invite-member-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          )}
        </div>
      </div>

      {/* Team Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-airbnb-light-pink rounded-lg">
              <Users className="w-5 h-5 text-airbnb-pink" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{members.length}</p>
              <p className="text-sm text-gray-500">Total Members</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Crown className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{members.filter(m => m.role === 'owner').length}</p>
              <p className="text-sm text-gray-500">Owners</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{members.filter(m => m.role === 'admin').length}</p>
              <p className="text-sm text-gray-500">Admins</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <UserCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{members.filter(m => m.status === 'active' || !m.status).length}</p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </div>
        </div>
      </div>

      {/* Team Members List */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="team-members-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map((member) => {
                const roleInfo = getRoleInfo(member.role);
                const statusInfo = getStatusInfo(member.status);
                const RoleIcon = roleInfo.icon;
                const isCurrentUser = member.user_id === user?.user_id;
                const canManage = isOwner || (isAdmin && member.role !== 'owner' && member.role !== 'admin');

                return (
                  <tr key={member.user_id} className={`hover:bg-gray-50 ${isCurrentUser ? 'bg-blue-50/30' : ''}`} data-testid={`team-member-${member.user_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {member.picture ? (
                          <img src={member.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-airbnb-light-pink flex items-center justify-center text-airbnb-pink font-bold">
                            {member.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">{member.name}</p>
                            {isCurrentUser && <Badge variant="outline" className="text-xs">You</Badge>}
                          </div>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={`${roleInfo.color} gap-1`}>
                        <RoleIcon className="w-3 h-3" />
                        {roleInfo.label}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {!isCurrentUser && canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`member-actions-${member.user_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {isOwner && member.role !== 'owner' && (
                              <DropdownMenuItem onClick={() => openRoleDialog(member)}>
                                <UserCog className="w-4 h-4 mr-2" />
                                Change Role
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openStatusDialog(member)}>
                              <Shield className="w-4 h-4 mr-2" />
                              {member.status === 'active' ? 'Disable Account' : 'Enable Account'}
                            </DropdownMenuItem>
                            {isOwner && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-red-600" onClick={() => openRemoveDialog(member)}>
                                  <UserMinus className="w-4 h-4 mr-2" />
                                  Remove from Team
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                );
              })}
              {members.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium">No team members yet</p>
                    <p className="text-sm">Invite team members to collaborate on your properties</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Permissions Info */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Role Permissions</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          {Object.entries(ROLES).map(([key, role]) => {
            const Icon = role.icon;
            return (
              <div key={key} className="p-4 rounded-lg bg-gray-50 border">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={role.color}>
                    <Icon className="w-3 h-3 mr-1" />
                    {role.label}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600">{role.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation email to add a new team member. They will receive temporary login credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="invite-name">Name</Label>
              <Input
                id="invite-name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="John Doe"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="john@example.com"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteForm.role} onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}>
                <SelectTrigger id="invite-role" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin - Can manage properties and team</SelectItem>
                  <SelectItem value="member">Member - Basic access</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting} data-testid="send-invite-btn">
              {inviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Change {selectedMember?.name}'s role. This affects their permissions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {['admin', 'member'].map((role) => {
              const info = ROLES[role];
              const Icon = info.icon;
              return (
                <button
                  key={role}
                  className={`w-full p-4 rounded-lg border text-left hover:border-airbnb-pink transition-colors ${selectedMember?.role === role ? 'border-airbnb-pink bg-airbnb-light-pink' : ''}`}
                  onClick={() => handleRoleChange(role)}
                  data-testid={`role-option-${role}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-medium text-gray-900">{info.label}</p>
                      <p className="text-sm text-gray-500">{info.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Status Dialog */}
      <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedMember?.status === 'active' ? 'Disable Account?' : 'Enable Account?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedMember?.status === 'active'
                ? `${selectedMember?.name} will no longer be able to log in or access your properties.`
                : `${selectedMember?.name} will be able to log in and access your properties again.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleStatusChange(selectedMember?.status === 'active' ? 'disabled' : 'active')}
              className={selectedMember?.status === 'active' ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="confirm-status-change-btn"
            >
              {selectedMember?.status === 'active' ? 'Disable Account' : 'Enable Account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Member Dialog */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {selectedMember?.name} from your team. They will lose access to all properties and data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-remove-btn">
              Remove from Team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
